import { primaryPrice } from "./money";
import type { CatalogProduct, PreferenceRecord, RecentInterestRecord, SearchCriteria, SearchSort } from "./types";

function productText(product: CatalogProduct): string {
  return [
    product.title,
    product.description,
    product.fixtureDepartment,
    product.inferredMetadata.attributes?.join(" "),
    product.inferredMetadata.techSpecs?.join(" "),
    product.inferredMetadata.topFeatures?.join(" "),
  ].filter(Boolean).join(" ").toLocaleLowerCase();
}

function matchesRequired(product: CatalogProduct, preferences: readonly PreferenceRecord[], criteria: SearchCriteria): boolean {
  const variant = product.variants[0];
  if ((criteria.filters?.available ?? true) && variant?.available === false) return false;
  const price = criteria.filters?.price;
  if (price && variant) {
    if (variant.price.currency !== price.currency) return false;
    if (price.min !== undefined && variant.price.amount < price.min) return false;
    if (price.max !== undefined && variant.price.amount > price.max) return false;
  }
  const text = productText(product);
  return preferences.every((record) => {
    const rule = record.rule;
    if (rule.strength !== "required" && rule.strength !== "avoid") return true;
    if (typeof rule.value !== "string") {
      if (rule.field === "price_min" || rule.field === "price_max") {
        if (!variant || variant.price.currency !== rule.value.currency) return false;
        return rule.field === "price_min" ? variant.price.amount >= rule.value.amount : variant.price.amount <= rule.value.amount;
      }
      return true;
    }
    if (["category", "brand", "material", "color", "style", "size", "compatibility", "merchant_region"].includes(rule.field)) {
      const found = text.includes(rule.value.toLocaleLowerCase());
      return rule.strength === "avoid" ? !found : found;
    }
    return true;
  });
}

function score(product: CatalogProduct, query: string, preferences: readonly PreferenceRecord[], interests: readonly RecentInterestRecord[]): number {
  const text = productText(product);
  const queryTokens = query.toLocaleLowerCase().split(/\s+/u).filter((token) => token.length > 2);
  let value = queryTokens.reduce((sum, token) => sum + (text.includes(token) ? 8 : 0), 0);
  for (const record of preferences) {
    if (typeof record.rule.value !== "string" || record.rule.strength !== "preferred") continue;
    if (text.includes(record.rule.value.toLocaleLowerCase())) value += record.source === "user" ? 6 : 4;
  }
  for (const interest of interests) {
    const overlap = interest.query.toLocaleLowerCase().split(/\s+/u).some((token) => token.length > 3 && text.includes(token));
    if (overlap) value += interest.strength === "active" ? 5 : interest.strength === "exploring" ? 3 : 1;
  }
  value += product.evidence.filter((item) => item.kind === "merchant_fact" || item.kind === "productist_check").length;
  value += (product.rating?.value ?? 0) / 10;
  return value;
}

function currencyStablePriceSort(products: readonly CatalogProduct[], direction: 1 | -1): CatalogProduct[] {
  const currencyOrder: string[] = [];
  for (const product of products) {
    const currency = primaryPrice(product)?.currency;
    if (currency && !currencyOrder.includes(currency)) currencyOrder.push(currency);
  }
  return [...products].sort((a, b) => {
    const ap = primaryPrice(a);
    const bp = primaryPrice(b);
    if (!ap) return 1;
    if (!bp) return -1;
    if (ap.currency !== bp.currency) return currencyOrder.indexOf(ap.currency) - currencyOrder.indexOf(bp.currency);
    return (ap.amount - bp.amount) * direction;
  });
}

export function rankProducts(
  products: readonly CatalogProduct[],
  criteria: SearchCriteria,
  preferences: readonly PreferenceRecord[],
  interests: readonly RecentInterestRecord[],
  sort: SearchSort = "best_match",
): readonly CatalogProduct[] {
  const eligible = products.filter((product) => matchesRequired(product, preferences, criteria));
  if (sort === "price_asc") return currencyStablePriceSort(eligible, 1);
  if (sort === "price_desc") return currencyStablePriceSort(eligible, -1);
  if (sort === "rating_desc") return [...eligible].sort((a, b) => (b.rating?.value ?? -1) - (a.rating?.value ?? -1));
  const ranked = [...eligible].sort((a, b) => score(b, criteria.query, preferences, interests) - score(a, criteria.query, preferences, interests));
  const diversified: CatalogProduct[] = [];
  const remaining = [...ranked];
  let previousMerchant = "";
  while (remaining.length > 0) {
    const differentMerchant = remaining.findIndex((product) => product.variants[0]?.merchant.id !== previousMerchant);
    const index = differentMerchant >= 0 ? differentMerchant : 0;
    const [next] = remaining.splice(index, 1);
    if (!next) break;
    diversified.push(next);
    previousMerchant = next.variants[0]?.merchant.id ?? "";
  }
  return diversified;
}
